import React, { useRef } from 'react';

import { after } from 'lodash';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';

import { getMonthRangeDates, getWeekRangeDates } from '../ScheduleUtils';
import { useAgendaContext } from './AgendaContext';
import AgendaDatePicker from './AgendaDatePicker';
import AgendaEvent from './AgendaEvent';
import AgendaHeader from './AgendaHeader';

const EmptyStateContainer = styled.div`
    height: ${(props) =>
        props.$stretchToParent
            ? '100%'
            : props.$isModelPage
              ? '384px'
              : props.$isBooking
                ? 'calc(100vh - 300px)'
                : '304px'};
    overflow: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    ${(props) =>
        props.$stretchToParent &&
        `
        flex: 1 1 auto;
        min-height: 0;
    `}
`;

const BookingAgendaContainer = styled.div`
    padding: 0 16px;
    position: relative;

    .agenda-container-body {
        display: flex;

        .agenda-container-left {
            max-width: 280px;
        }

        .agenda-container-right {
            flex: 1 0 0;
            padding-left: 1rem;
            padding-top: 0.5rem;

            .events-list-container {
                padding-left: 1rem;
                height: calc(100vh - 250px);
                overflow: auto;

                &.model-selected {
                    height: calc(100vh - 300px);
                }
            }
        }
    }
`;

const ContactAgendaContainer = styled.div`
    padding: 0 0.5rem;
    position: relative;
    display: flex;
    ${(props) =>
        props.$stretchToParent &&
        `
        flex: 1 1 auto;
        min-height: 0;
    `}

    .agenda-container-left {
        max-width: 20rem;
    }

    .agenda-container-right {
        flex: 1 0 0;
        padding-left: 2rem;
        ${(props) =>
            props.$stretchToParent &&
            `
            display: flex;
            flex-direction: column;
            min-width: 0;
            min-height: 0;
        `}

        .events-list-container {
            padding-left: 1rem;
            height: ${(props) => (props.$stretchToParent ? 'auto' : '304px')};
            overflow: auto;
            ${(props) =>
                props.$stretchToParent &&
                `
                flex: 1 1 auto;
                min-height: 0;
            `}

            &.model {
                height: ${(props) => (props.$stretchToParent ? 'auto' : '434px')};
            }
        }
    }
`;

const AgendaContainer = ({ stretchToParent = false }) => {
    const {
        contactId,
        bookingSection,
        eventsToDisplay,
        isLoadingDay,
        isLoadingMonth,
        onScheduleRangeChange,
        scheduleRange,
        type,
    } = useAgendaContext();

    const eventsScroller = useRef();

    const handleChangeCalendarStyle = (view) => {
        const startdate = getMoment(scheduleRange.start);
        switch (view) {
            case 'month':
                const monthdates = getMonthRangeDates(startdate);
                onScheduleRangeChange(monthdates.start, monthdates.end);
                break;
            case 'week':
                const weekdates = getWeekRangeDates(startdate);
                onScheduleRangeChange(weekdates.start, weekdates.end);
                break;
            default:
                console.error('stay in agenda mode');
        }
    };

    const afterDateSelection = () => {
        if (eventsScroller.current) {
            eventsScroller.current.scrollTo(0, 0);
        }
    };

    return isLoadingMonth ? (
        <Loading paddingTop="6rem" textBlack />
    ) : (
        <>
            {bookingSection ? (
                <BookingAgendaContainer>
                    <div className="agenda-container-header">
                        <AgendaHeader onChangeCalendarStyle={handleChangeCalendarStyle} />
                    </div>
                    <div className="agenda-container-body">
                        <div className="agenda-container-left">
                            <AgendaDatePicker afterDateSelection={afterDateSelection} />
                        </div>
                        <div className="agenda-container-right">
                            {isLoadingDay ? (
                                <Loading small paddingTop="6rem" textBlack />
                            ) : eventsToDisplay && eventsToDisplay.length ? (
                                <div
                                    className={`events-list-container ${contactId !== null ? 'model-selected' : ''}`}
                                    ref={eventsScroller}
                                >
                                    {eventsToDisplay.map((ev, index) => (
                                        <AgendaEvent key={`${ev.TypeName}-${ev.ID}-${index}`} event={ev} />
                                    ))}
                                </div>
                            ) : (
                                <EmptyStateContainer $stretchToParent={stretchToParent}>
                                    <EmptyState message="no events" />
                                </EmptyStateContainer>
                            )}
                        </div>
                    </div>
                </BookingAgendaContainer>
            ) : (
                <ContactAgendaContainer $stretchToParent={stretchToParent}>
                    <div className="agenda-container-left">
                        <AgendaDatePicker />
                    </div>
                    <div className="agenda-container-right">
                        <AgendaHeader onChangeCalendarStyle={handleChangeCalendarStyle} />
                        {isLoadingDay ? (
                            <Loading />
                        ) : eventsToDisplay && eventsToDisplay.length ? (
                            <div className={`events-list-container ${type === 'model' ? 'model' : ''}`}>
                                {eventsToDisplay.map((ev, index) => (
                                    <AgendaEvent key={`${ev.TypeName}-${ev.ID}-${index}`} event={ev} />
                                ))}
                            </div>
                        ) : (
                            <EmptyStateContainer $stretchToParent={stretchToParent}>
                                <EmptyState message="no events" />
                            </EmptyStateContainer>
                        )}
                    </div>
                </ContactAgendaContainer>
            )}
        </>
    );
};

export default AgendaContainer;
